import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon } from '@repo/ui';
import { Alert, Close, LoaderGradient } from '@repo/ui/icons';
import type { VaultV2RecapSource } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import type { ForceDeallocationContext } from '@/components/common/VaultV2FeeRow';
import type { CheckoutAttemptTrackingContext, ReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { useVaultV2RecapData } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import VaultV2Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV2Recap';
import { ErrorServiceUnavailable } from '@/components/common/Error/ErrorServiceUnavailable';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { useWeb3Context } from '@/contexts/Web3Context';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import { useDeferredSubmit } from '@/hooks/useDeferredSubmit';
import { useReviewFunnelTracking } from '@/lib/analytics/client/hooks/useReviewFunnelTracking';
import type { SimulationStatus } from '../../../hooks/useVaultV2SimulatedFinalPosition';
interface VaultV2OperationReviewDialogProps {
  type: 'vaultV2Deposit' | 'vaultV2Withdraw';
  amount: bigint;
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  vaultAddress: Address;
  isButtonDisabled: boolean;
  /** First preparation, nothing to show yet: skeleton recap + loading CTA. */
  isLoading: boolean;
  /**
   * A block-poll refresh (rebuild / requirements re-key / re-sim) is in flight behind values
   * already on screen. The recap and CTA stay as they are; a click lands once it settles.
   */
  isRefreshing?: boolean;
  handleContinue: (tracking: CheckoutAttemptTrackingContext) => void;
  reviewTrackingParams: ReviewDialogParams;
  feeAssets?: bigint;
  /** What a buffered MAX `forceRedeem` leaves deposited and never pays out; 0n elsewhere. */
  retainedAssets?: bigint;
  forceDeallocation?: ForceDeallocationContext;
  /** Outcome of the single review-time asset-changes simulation. */
  simulationStatus: SimulationStatus;
  /** A simulation ran but couldn't verify the position-after, so it's an estimate. */
  isUnverifiedFinalPosition: boolean;
  /**
   * Reason the action can't be prepared or signed. When set, the disabled Continue button is
   * paired with a `role="alert"` explanation instead of being silently inert.
   */
  errorReason?: string;
  /** The procedure the recap reads, matching the dialog the review was opened from. */
  recapSource?: VaultV2RecapSource;
}
const VaultV2OperationReviewDialog = t0 => {
  const $ = _c(51);
  const {
    type,
    amount,
    initialPosition,
    finalPosition,
    vaultAddress,
    isButtonDisabled,
    isLoading,
    isRefreshing: t1,
    handleContinue,
    reviewTrackingParams,
    feeAssets,
    retainedAssets,
    forceDeallocation,
    simulationStatus,
    isUnverifiedFinalPosition,
    errorReason,
    recapSource
  } = t0;
  const isRefreshing = t1 === undefined ? false : t1;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    account,
    switchToTargetChain
  } = useWeb3Context();
  useCloseDialogOnWalletChange();
  const {
    trackClose,
    confirmAttempt
  } = useReviewFunnelTracking(reviewTrackingParams, !isLoading);
  const isSimulationLoading = simulationStatus === "loading";
  const isSimulationBlacklisted = simulationStatus === "blacklist";
  let t2;
  if ($[0] !== recapSource || $[1] !== vaultAddress) {
    t2 = {
      vaultAddress,
      source: recapSource
    };
    $[0] = recapSource;
    $[1] = vaultAddress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const recap = useVaultV2RecapData(t2);
  const isRecapMissing = !recap.vaultV2 || !recap.vaultAsset;
  const canSubmit = !isButtonDisabled && !isLoading && !isSimulationLoading && !isSimulationBlacklisted && !isRecapMissing;
  const t3 = isButtonDisabled || isSimulationBlacklisted || isRecapMissing && !recap.loading;
  let t4;
  if ($[3] !== confirmAttempt || $[4] !== handleContinue) {
    t4 = () => handleContinue(confirmAttempt());
    $[3] = confirmAttempt;
    $[4] = handleContinue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== canSubmit || $[7] !== isRefreshing || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      isRefreshing,
      canSubmit,
      shouldCancel: t3,
      submit: t4
    };
    $[6] = canSubmit;
    $[7] = isRefreshing;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    request: requestContinue,
    isPending: isContinuePending
  } = useDeferredSubmit(t5);
  const isButtonLoading = isLoading || isSimulationLoading || recap.loading || isContinuePending;
  const isDisabled = !canSubmit || isContinuePending;
  let t6;
  if ($[11] !== toggleDialog || $[12] !== trackClose) {
    t6 = () => {
      trackClose();
      toggleDialog(false);
    };
    $[11] = toggleDialog;
    $[12] = trackClose;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleClose = t6;
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-title-sm">Review</span>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Close />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== handleClose) {
    t9 = <div className="flex items-center justify-between gap-[16px]">{t7}<Button size="icon" onClick={handleClose} variant="secondary" aria-label="Close">{t8}</Button></div>;
    $[16] = handleClose;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ErrorServiceUnavailable minHeight="364px" />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== amount || $[20] !== feeAssets || $[21] !== finalPosition || $[22] !== forceDeallocation || $[23] !== initialPosition || $[24] !== isSimulationLoading || $[25] !== isUnverifiedFinalPosition || $[26] !== recapSource || $[27] !== retainedAssets || $[28] !== type || $[29] !== vaultAddress) {
    t11 = <SectionErrorBoundary fallback={t10}><VaultV2Recap type={type} amount={amount} initialPosition={initialPosition} finalPosition={finalPosition} vaultAddress={vaultAddress} feeAssets={feeAssets} retainedAssets={retainedAssets} forceDeallocation={forceDeallocation} isFinalPositionLoading={isSimulationLoading} isSimulationFallback={isUnverifiedFinalPosition} source={recapSource} /></SectionErrorBoundary>;
    $[19] = amount;
    $[20] = feeAssets;
    $[21] = finalPosition;
    $[22] = forceDeallocation;
    $[23] = initialPosition;
    $[24] = isSimulationLoading;
    $[25] = isUnverifiedFinalPosition;
    $[26] = recapSource;
    $[27] = retainedAssets;
    $[28] = type;
    $[29] = vaultAddress;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== isSimulationBlacklisted) {
    t12 = isSimulationBlacklisted && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="vault-v2-blacklist-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">This transaction can't be completed due to a simulation error.</span></div>;
    $[31] = isSimulationBlacklisted;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== errorReason || $[34] !== isButtonLoading || $[35] !== isSimulationBlacklisted) {
    t13 = !isSimulationBlacklisted && !isButtonLoading && errorReason && <div className="gap-2xs flex flex-row items-center" role="alert" data-testid="vault-v2-preparation-error"><Icon className="text-destructive-foreground" icon={<Alert />} /><span className="text-destructive-foreground text-xs">{errorReason}</span></div>;
    $[33] = errorReason;
    $[34] = isButtonLoading;
    $[35] = isSimulationBlacklisted;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== account.isWrongChain || $[38] !== isButtonLoading || $[39] !== isDisabled || $[40] !== isRefreshing || $[41] !== requestContinue || $[42] !== switchToTargetChain || $[43] !== type) {
    t14 = account.isWrongChain ? <Button size="lg" onClick={switchToTargetChain}>Switch chain</Button> : <Button data-testid="confirm-button" data-refreshing={isRefreshing} onClick={() => requestContinue()} size="lg" disabled={isDisabled}>{isButtonLoading ? <LoaderGradient /> : null}{isButtonLoading ? "Loading" : type === "vaultV2Deposit" ? "Deposit" : "Withdraw"}</Button>;
    $[37] = account.isWrongChain;
    $[38] = isButtonLoading;
    $[39] = isDisabled;
    $[40] = isRefreshing;
    $[41] = requestContinue;
    $[42] = switchToTargetChain;
    $[43] = type;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t11 || $[46] !== t12 || $[47] !== t13 || $[48] !== t14 || $[49] !== t9) {
    t15 = <div className="gap-md flex flex-col" data-testid="confirm-dialog">{t9}{t11}{t12}{t13}{t14}</div>;
    $[45] = t11;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
    $[49] = t9;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
};
export default VaultV2OperationReviewDialog;